import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { Suspense, useContext } from 'react';
import { useParams } from 'react-router';
import { useRSimSessionData } from './useRSimSessionData';

import { PollingChart } from './PollingChart';
import { SessionHistoryContext } from './SessionHistoryContext';
import { SessionHistoryProvider } from './SessionHistoryProvider';
import { SessionChart } from './SessionChart';
import { UsageChart } from './UsageChart';
import { SessionTooltip } from './SessionTooltip';
import { ControlPanel } from 'common/components/Graphs/ControlPanel';
import Stack from '@mui/material/Stack';
import { SessionDetailsWithInterimsGraphs } from 'common/components/SessionHistory/components/SessionDetailsWithInterimsGraphs';
import { GraphSessionRangeProvider } from 'common/components/Graphs/Sessions/GraphSessionRangeProvider';
import { ScrollArea } from 'src/common/components/ScrollArea/ScrollArea';
import { styled } from '@mui/material/styles';
import { TabPanelWithErrorHandling } from 'src/common/components/AppLayout/Components/TabPanelWithErrorHandling';
import { useTranslation } from 'react-i18next';

const ChartArea = () => {
  const { sessionToDisplay, sessionToDisplaySelection, primaryIccid, secondaryIccid } =
    useContext(SessionHistoryContext);

  let iccid = '-';
  let profile: string | null = '-';
  if (sessionToDisplay) {
    iccid = sessionToDisplaySelection.setName === 'Primary' ? primaryIccid : secondaryIccid;
    profile = sessionToDisplaySelection.setName;
  }

  return (
    <Stack spacing={8}>
      <Box>
        <PollingChart />
      </Box>
      <SessionChart />
      <UsageChart />
      <Box mt={8} pl={40}>
        <SessionDetailsWithInterimsGraphs
          session={sessionToDisplay}
          profile={profile}
          iccid={iccid}
        />
      </Box>
    </Stack>
  );
};

const RSimHistoryAnalysisContentRoot = styled('div', { name: 'RSimHistoryAnalysisContentRoot' })({
  position: 'relative',
  height: '100%',
  width: '100%',
});

const RSimHistoryAnalysisContent = () => {
  const { euicc } = useParams();

  const rsimSessionData = useRSimSessionData(euicc!);

  return (
    <GraphSessionRangeProvider>
      <ScrollArea style={{ height: '100%' }}>
        <SessionHistoryProvider
          primaryImsi={rsimSessionData.primarySimProfile}
          secondaryImsi={rsimSessionData.secondarySimProfile}
          primaryIccid={rsimSessionData.primaryIccid}
          secondaryIccid={rsimSessionData.secondaryIccid}
        >
          <Box
            sx={{
              position: 'relative',
              height: '100%',
              pt: 8,
            }}
          >
            <SessionTooltip containerWidth={100} />
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                position: 'relative',
              }}
              spacing={8}
            >
              <Box
                sx={{
                  px: 16,
                  pt: 8,
                  pb: 8,
                  borderBottom: (theme) => `1px solid ${theme.palette.border}`,
                }}
              >
                <ControlPanel
                  sliderSx={{
                    pl: 64,
                    pr: 24,
                  }}
                />
              </Box>
              <ChartArea />
            </Stack>
          </Box>
        </SessionHistoryProvider>
      </ScrollArea>
    </GraphSessionRangeProvider>
  );
};

export const RSimHistoryAnalysis = () => {
  const { t } = useTranslation();
  return (
    <TabPanelWithErrorHandling name={t('rSimInventory.details.tabs.historyAnalysis')}>
      <RSimHistoryAnalysisContentRoot>
        <Suspense
          fallback={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <RSimHistoryAnalysisContent />
        </Suspense>
      </RSimHistoryAnalysisContentRoot>
    </TabPanelWithErrorHandling>
  );
};
